import { X, CreditCard, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { OrderWithTax, OrderItemWithModifiers } from "@/types/pos";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import placeholder from "@/assets/pos/placeholder.png";

interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  data: OrderWithTax | null;
}

export default function DetailModal({ isOpen, onClose, title = "Order Details", data }: DetailModalProps) {
  const { formatDate } = useDateFormatter();

  const renderItem = (item: OrderItemWithModifiers) => {
    const priceUsd = Number(item?.unitPriceUsd ?? 0);
    const priceSech = Number(item?.unitPriceSech ?? 0);
    const qty = item?.quantity ?? 1;
    const imageSrc = item.product?.imageUrl
      ? item?.product?.imageUrl
      : item?.customDrink?.components?.find((c) => c.type === "spirit" && c.imageUrl)?.imageUrl || placeholder;

    return (
      <div key={item?.id} className="pb-3 mb-3 rounded-md border-dashed border-b border-[var(--layout-border-color)]">
        <div className="flex items-start gap-2">
          <div className="h-14 w-14 rounded-md overflow-hidden bg-[var(--secondary-background-color)] flex-shrink-0 flex items-center justify-center">
            <img
              src={imageSrc}
              alt={item?.product?.name ?? item?.customDrink?.name ?? "Order item"}
              className="h-full w-full object-cover"
            />
          </div>

          <div className="flex-1">
            <article className="menu-item">
              <h3 className="font-montserrat mb-1 text-sm leading-[18px] text-[var(--primary-text-color)] font-semibold">
                {item?.product?.name || item?.customDrink?.name}
              </h3>

              <p className="font-figtree text-[13px] font-bold mb-1">
                <span className="text-[#258750] mr-2">
                  {(priceSech * qty).toLocaleString()} SECH
                </span>
                <span className="text-[var(--primary-text-color)]">
                  ${(priceUsd * qty).toFixed(2)}
                </span>
              </p>

              {item?.product?.description && (
                <p className="font-figtree text-[13px] text-[var(--secondary-text-color)]">
                  {item?.product?.description ??
                    (item?.customDrink
                      ? item?.customDrink?.components?.map((c) => c.name).join(", ")
                      : "")}
                </p>
              )}

              {item?.customDrink && item?.customDrink?.components && item?.customDrink?.components.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {item?.customDrink?.components.map((c) => (
                    <li key={c.id} className="text-xs text-[var(--secondary-text-color)] flex justify-between">
                      <span>{c.name}</span>
                      <span>${c.priceUsd}</span>
                    </li>
                  ))}
                </ul>
              )}

              {item?.modifiers && item.modifiers.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {item.modifiers.map((mod) => (
                    <li key={mod.id} className="text-xs text-[var(--secondary-text-color)] flex justify-between">
                      <span>{mod.modifierName}</span>
                      {Number(mod.priceAdjustmentUsd) !== 0 && (
                        <span className={Number(mod.priceAdjustmentUsd) > 0 ? "text-[var(--primary-text-color)]" : "text-[#258750]"}>
                          {Number(mod.priceAdjustmentUsd) > 0 ? "+" : "-"}${Math.abs(Number(mod.priceAdjustmentUsd)).toFixed(2)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          </div>
        </div>

        <div className="flex justify-between items-center h-[26px] mt-2">
          <span className="text-[13px] text-[var(--secondary-text-color)]">
            Qty: {qty}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      aria-hidden={!isOpen}
      className={`fixed inset-0 z-50 pointer-events-none ${isOpen ? "" : ""}`}
    >
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />

      <aside
        className={`fixed top-0 right-0 h-full w-full max-w-[392px] md:max-w-[392px] lg:max-w-[520px] bg-[var(--primary-background-color)] shadow-2xl transform transition-transform duration-300 pointer-events-auto
          ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-[44px] px-5 border-b border-[var(--layout-border-color)]">
            <div className="flex items-center gap-3">
              <h3 className="text-[18px] leading-[26px] font-bold">{title}</h3>
            </div>
            <div className="flex items-center gap-2 h-6 w-6 rounded-full hover:bg-[var(--hover-background-color)] cursor-pointer">
              <Button variant="ghost" onClick={onClose} className="h-6 w-6 p-0 rounded-full">
                <X />
              </Button>
            </div>
          </div>

          <div className="flex flex-col flex-1 overflow-hidden">
            {!data ? (
              <div className="text-center text-[var(--secondary-text-color)] p-5">No order selected</div>
            ) : (
              <div className="flex flex-col flex-1 overflow-hidden">
                <div className="overflow-y-auto p-5 pb-0 flex-1">
                  <section className="grid grid-cols-2 gap-6 items-stretch mb-3 pb-4 border-dashed border-b border-[var(--layout-border-color)]">
                    <article className="rounded-md h-full">
                      <dt className="text-[13px] leading-[15px] font-medium text-[var(--primary-text-color)]">Order Number</dt>
                      <dd className="mt-1 text-[13px] leading-[15px] font-medium text-[var(--secondary-text-color)] break-all">{data.orderNumber}</dd>
                    </article>

                    <article className="rounded-md h-full">
                      <dt className="text-[13px] leading-[15px] font-medium text-[var(--primary-text-color)]">Order Date</dt>
                      <dd className="mt-1 text-[13px] leading-[15px] font-medium text-[var(--secondary-text-color)] break-all">{formatDate(data.createdAt)}</dd>
                    </article>

                    <article className="rounded-md h-full">
                      <dt className="text-[13px] leading-[15px] font-medium text-[var(--primary-text-color)]">Order Status</dt>
                      <dd className="mt-1 text-[13px] leading-[15px] font-medium text-[var(--secondary-text-color)] break-all">
                        <span className={`status ${data.status}`}>{data.status.charAt(0).toUpperCase() + data.status.slice(1)}</span>
                      </dd>
                    </article>

                    <article className="rounded-md h-full">
                      <dt className="text-[13px] leading-[15px] font-medium text-[var(--primary-text-color)]">Pickup Person</dt>
                      <dd className="mt-1 text-[13px] leading-[15px] font-medium text-[var(--secondary-text-color)] break-all">{data.user?.pickupPerson || "-"}</dd>
                    </article>

                    <article className="rounded-md h-full">
                      <dt className="text-[13px] leading-[15px] font-medium text-[var(--primary-text-color)]">Payment Method</dt>
                      <dd className="mt-1">
                        {(() => {
                          const isCard = data.paymentMethod === 'CARD' || (!data.paymentMethod && !data.transactionHash);
                          return isCard ? (
                            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">
                              <CreditCard className="h-2.5 w-2.5" />Card
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-700">
                              <Wallet className="h-2.5 w-2.5" />Token
                            </span>
                          );
                        })()}
                        {data.transactionHash && (
                          <dd className="mt-1 text-[11px] leading-[14px] font-medium text-[var(--secondary-text-color)] font-mono break-all">
                            {data.transactionHash.slice(0, 6)}...{data.transactionHash.slice(-4)}
                          </dd>
                        )}
                      </dd>
                    </article>

                    <article className="rounded-md h-full">
                      <dt className="text-[13px] leading-[15px] font-medium text-[var(--primary-text-color)]">Location Details</dt>
                      <dd className="mt-1 text-[13px] leading-[15px] font-medium text-[var(--secondary-text-color)] break-all">
                        {data?.locationDetails ? data.locationDetails : "N/A"}
                      </dd>
                    </article>

                    <article className="rounded-md h-full">
                      <dt className="text-[13px] leading-[15px] font-medium text-[var(--primary-text-color)]">Location</dt>
                      <dd className="mt-1 text-[13px] leading-[15px] font-medium text-[var(--secondary-text-color)] break-all">
                        {data.location?.name || "-"}
                      </dd>
                      {data.location?.colorCode && (
                        <dd className="mt-1 flex items-center gap-2 text-[13px] leading-[15px] font-medium text-[var(--secondary-text-color)]">
                          <span className="h-4 w-4 rounded" style={{ backgroundColor: data.location.colorCode }} />
                          <span>{data.location?.description || ""}</span>
                        </dd>
                      )}
                    </article>
                  </section>

                  {data.notes && (
                    <section className="mb-3 pb-4 border-dashed border-b border-[var(--layout-border-color)]">
                      <article className="rounded-md">
                        <dt className="text-[13px] leading-[15px] font-medium text-[var(--primary-text-color)]">Special Notes</dt>
                        <dd className="mt-1 text-[13px] leading-[15px] font-medium text-[var(--secondary-text-color)] bg-yellow-50 p-2 rounded border border-yellow-200">{data.notes}</dd>
                      </article>
                    </section>
                  )}

                  <div>
                    {data.items && data.items.map((item) => renderItem(item))}
                  </div>
                </div>

                <div className="p-5 pt-3 border-t border-[var(--layout-border-color)] bg-[var(--primary-background-color)]">
                  <div className="border-dashed border-b border-[var(--layout-border-color)] mb-3 pb-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-[13px] leading-4 font-medium font-figtree text-[var(--secondary-text-color)]">Subtotal</span>
                      <span className="text-[13px] leading-4 font-medium font-figtree text-[var(--secondary-text-color)]">${data.subtotal}</span>
                    </div>
                    {data.discountAmount && parseFloat(data.discountAmount) > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[13px] leading-4 font-medium font-figtree text-[#22C55E]">
                          Discount{data.discountCode && ` (${data.discountCode})`}
                        </span>
                        <span className="text-[13px] leading-4 font-medium font-figtree text-[#22C55E]">
                          -${parseFloat(data.discountAmount).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {data.taxBreakdown?.map((tax, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-[13px] leading-4 font-medium font-figtree text-[var(--secondary-text-color)]">
                          {(tax?.rate * 100).toFixed(0)}% {tax?.type?.replace(/[_-]+/g, " ")?.toLowerCase()?.replace(/\b\w/g, (char) => char.toUpperCase())}
                        </span>
                        <span className="text-[13px] leading-4 font-medium font-figtree text-[var(--secondary-text-color)]">
                          ${parseFloat(String(tax?.amountUSD ?? 0)).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm leading-[18px] font-bold font-figtree text-[var(--primary-text-color)]">Total</span>
                    <span className="text-sm leading-[18px] font-bold font-figtree text-[var(--primary-text-color)]">
                      {(data.paymentMethod === 'CARD' || (!data.paymentMethod && !data.transactionHash))
                        ? `$${data.totalUsd} USD`
                        : `${parseFloat(data.totalSech ?? "0").toFixed(2)} SECH`
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
